/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2026 (original work) Open Assessment Technologies SA
 */

import {
    convertDateStringWithTimeZoneToDate,
    ensureMomentLocale,
    formatMomentWithPattern,
    getApplicationLocale
} from '@oat-sa-private/ui-core';
import {
    isLocalizedFormatsEnabled,
    resolveDisplayFormat,
    getLegacyDisplayFormat
} from '@/core/utils/dateFormatConfig.js';

const LEGACY_DATETIME_ALT_FORMAT = 'MMM D, YYYY [at] h:mm a';

// ISO datetime with an explicit zone, e.g. 2026-08-11T14:14:00.000Z or 2026-08-11T16:14+02:00
const ZONED_ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/i;

/**
 * Parses strings that already carry a zone as a fixed point in time.
 * The timezone-aware parser keeps the wall clock instead, which moves
 * bounds like minDate/maxDate by the browser offset.
 * @param {string} dateString
 * @returns {Date|null}
 */
function parseZonedDateString(dateString) {
    if (typeof dateString !== 'string' || !ZONED_ISO_DATETIME.test(dateString.trim())) {
        return null;
    }

    const parsed = new Date(dateString);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Resolves the flatpickr alt format for datetime inputs, including tenant config fallback.
 * @returns {string}
 */
function getFlatPickrDatetimeAltFormat() {
    const configuredFormat = resolveDisplayFormat({ hasHour: true });

    if (isLocalizedFormatsEnabled()) {
        return configuredFormat;
    }

    if (configuredFormat !== getLegacyDisplayFormat()) {
        return configuredFormat;
    }

    return LEGACY_DATETIME_ALT_FORMAT;
}

/**
 * Matches the flatpickr time spinner to the locale clock used in the visible input.
 * @param {string} [locale]
 * @returns {boolean}
 */
function prefers24HourClock(locale) {
    try {
        const hourCycle = new Intl.DateTimeFormat(locale || 'en', { hour: 'numeric' }).resolvedOptions()
            .hourCycle;
        return hourCycle === 'h23' || hourCycle === 'h24';
    } catch (e) {
        return false;
    }
}

/**
 * @param {string} timezone
 * @param {string} [locale]
 * @returns {object}
 */
export function getFlatPickrOptions(timezone, locale) {
    const resolvedLocale = locale ?? getApplicationLocale();

    // Fire-and-forget preload: flatpickr parseDate/formatDate callbacks are synchronous.
    void ensureMomentLocale(resolvedLocale);

    return {
        altInput: true,
        enableTime: true,
        allowInvalidPreload: true,
        time_24hr: prefers24HourClock(resolvedLocale),
        altFormat: getFlatPickrDatetimeAltFormat(),
        // Flatpickr default ariaDateFormat uses F/j/Y tokens; formatDate is moment-based.
        ariaDateFormat: 'MMMM D, YYYY',
        dateFormat: 'YYYY-MM-DD\\THH:mmZ',
        parseDate(dateString, format) {
            return (
                parseZonedDateString(dateString) ??
                convertDateStringWithTimeZoneToDate(dateString, format, timezone, resolvedLocale)
            );
        },
        formatDate(dateObj, format) {
            return formatMomentWithPattern(dateObj, format, resolvedLocale);
        },
        onReady(selectedDates, dateStr, instance) {
            if (instance.config.minDate) {
                const minDate = new Date(instance.config.minDate);
                const currentViewDate = new Date(instance.currentYear, instance.currentMonth);
                const minViewDate = new Date(minDate.getFullYear(), minDate.getMonth());

                if (currentViewDate < minViewDate) {
                    instance.changeYear(minDate.getFullYear());
                    instance.changeMonth(minDate.getMonth() - instance.currentMonth);
                }
            }
        }
    };
}
